import React, { useState, useRef } from 'react';
import { folderService } from '../../../services/api';

interface UploadDocumentModalProps {
  folderId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({ 
  folderId, 
  onClose, 
  onSuccess 
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: { progress: number, status: 'pending' | 'uploading' | 'completed' | 'error', error?: string }}>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    const errors: string[] = [];

    fileArray.forEach((file, index) => {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name}: El archivo no puede ser mayor a 10MB`);
        return;
      }

      // Check if file already exists
      if (selectedFiles.some(f => f.name === file.name && f.size === file.size)) {
        errors.push(`${file.name}: El archivo ya está seleccionado`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join(', '));
    } else {
      setError('');
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      // Initialize progress for new files
      const newProgress: {[key: string]: { progress: number, status: 'pending' | 'uploading' | 'completed' | 'error', error?: string }} = {};
      validFiles.forEach(file => {
        newProgress[file.name] = { progress: 0, status: 'pending' };
      });
      setUploadProgress(prev => ({ ...prev, ...newProgress }));
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedFiles.length === 0) {
      setError('Selecciona al menos un archivo');
      return;
    }

    setLoading(true);
    setError('');

    let successCount = 0;
    let errorCount = 0;

    // Upload files one by one
    for (const file of selectedFiles) {
      if (uploadProgress[file.name]?.status === 'completed') {
        successCount++;
        continue;
      }

      try {
        // Update progress to uploading
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { progress: 50, status: 'uploading' }
        }));

        const response = await folderService.uploadDocument(folderId, { file });
        
        if (response.success) {
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { progress: 100, status: 'completed' }
          }));
          successCount++;
        } else {
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { progress: 0, status: 'error', error: response.error || 'Error al subir' }
          }));
          errorCount++;
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { progress: 0, status: 'error', error: 'Error al subir el archivo' }
        }));
        errorCount++;
      }
    }

    setLoading(false);

    if (errorCount === 0) {
      // All files uploaded successfully
      onSuccess();
    } else if (successCount > 0) {
      // Some files uploaded successfully
      setError(`${successCount} archivo(s) subido(s) exitosamente, ${errorCount} archivo(s) fallaron`);
    } else {
      // All files failed
      setError('Error al subir los archivos');
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (fileName: string) => {
    setSelectedFiles(prev => prev.filter(file => file.name !== fileName));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📄';
      case 'xls':
      case 'xlsx': return '📈';
      case 'ppt':
      case 'pptx': return '📅';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return '🖼️';
      case 'zip':
      case 'rar': return '🗁';
      default: return '📄';
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Subir Documento</h2>
          <button onClick={onClose} className="close-btn">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="error-message">{error}</div>}
          
          <div 
            className={`file-drop-zone ${dragOver ? 'drag-over' : ''} ${selectedFiles.length > 0 ? 'has-files' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={selectedFiles.length === 0 ? triggerFileInput : undefined}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              disabled={loading}
            />
            
            {selectedFiles.length > 0 ? (
              <div className="selected-files">
                <div className="files-header">
                  <span>{selectedFiles.length} archivo(s) seleccionado(s)</span>
                  <button
                    type="button"
                    onClick={triggerFileInput}
                    className="btn-add-more"
                    disabled={loading}
                  >
                    + Añadir más
                  </button>
                </div>
                <div className="files-list">
                  {selectedFiles.map((file, index) => {
                    const progress = uploadProgress[file.name];
                    return (
                      <div key={`${file.name}-${index}`} className="file-item">
                        <div className="file-info">
                          <div className="file-icon">{getFileIcon(file.name)}</div>
                          <div className="file-details">
                            <p className="file-name">{file.name}</p>
                            <p className="file-size">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                            {progress && (
                              <div className="file-progress">
                                <div className={`progress-bar progress-${progress.status}`}>
                                  <div 
                                    className="progress-fill" 
                                    style={{ width: `${progress.progress}%` }}
                                  ></div>
                                </div>
                                <span className="progress-text">
                                  {progress.status === 'pending' && 'Pendiente'}
                                  {progress.status === 'uploading' && 'Subiendo...'}
                                  {progress.status === 'completed' && '✓ Completado'}
                                  {progress.status === 'error' && `✗ ${progress.error}`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        {!loading && progress?.status !== 'completed' && (
                          <button
                            type="button"
                            onClick={() => removeFile(file.name)}
                            className="btn-remove-file"
                            title="Eliminar archivo"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="drop-zone-content">
                <div className="upload-icon">
                  <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#9ca3af' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p>Arrastra archivos aquí o haz clic para seleccionar</p>
                <p className="file-limit">Máximo 10MB por archivo - Múltiples archivos permitidos</p>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || selectedFiles.length === 0}
            >
              {loading ? 'Subiendo...' : `Subir ${selectedFiles.length} Archivo(s)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadDocumentModal;